import { useState, useEffect, useRef } from 'react'
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Input,
  FormControl,
  FormLabel,
  Textarea,
  Switch,
  useToast,
  Badge,
  Spinner,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  useColorModeValue,
  useBreakpointValue,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerBody,
  SimpleGrid,
} from '@chakra-ui/react'
import { Plus, PencilSimple, Trash, List } from '@phosphor-icons/react'
import api from '../services/api'
import { Sidebar } from '../layouts/DefaultLayout'
import socketService, { SOCKET_EVENTS } from '../services/socket'

interface CustomStatus {
  id: number
  name: string
  color: string
  impactsSLA: boolean
  requiresReason: boolean
  description: string | null
  chatCount: number
}

export default function Status() {
  const [statuses, setStatuses] = useState<CustomStatus[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [selectedStatus, setSelectedStatus] = useState<CustomStatus | null>(null)
  const [formData, setFormData] = useState({
    name: '',
    color: '#808080',
    impactsSLA: true,
    requiresReason: false,
    description: '',
  })
  const [internalTicketsBadge, setInternalTicketsBadge] = useState(0)
  const toast = useToast()
  const isMobile = useBreakpointValue({ base: true, lg: false })
  const surfaceBg = useColorModeValue('white', 'gray.800')
  const borderColor = useColorModeValue('gray.200', 'gray.700')
  const textPrimary = useColorModeValue('gray.800', 'white')
  const textMuted = useColorModeValue('gray.500', 'gray.400')
  const menuIconColor = useColorModeValue('gray.700', 'white')

  const { isOpen: isFormOpen, onOpen: onFormOpen, onClose: onFormClose } = useDisclosure()
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure()
  const { isOpen: isMenuOpen, onOpen: onMenuOpen, onClose: onMenuClose } = useDisclosure()
  const cancelRef = useRef<HTMLButtonElement>(null)

  const fetchInternalTicketsCount = async () => {
    try {
      const res = await api.get('/internal-tickets/my-pending-count')
      setInternalTicketsBadge(res.data.count || 0)
    } catch {
      // ignore
    }
  }

  useEffect(() => {
    fetchInternalTicketsCount()
    const interval = setInterval(fetchInternalTicketsCount, 120000)

    const handleNew = () => fetchInternalTicketsCount()
    const handleReply = () => fetchInternalTicketsCount()
    const handleResolved = () => fetchInternalTicketsCount()

    socketService.on(SOCKET_EVENTS.INTERNAL_TICKET.NEW, handleNew)
    socketService.on(SOCKET_EVENTS.INTERNAL_TICKET.REPLY, handleReply)
    socketService.on(SOCKET_EVENTS.INTERNAL_TICKET.RESOLVED, handleResolved)

    return () => {
      clearInterval(interval)
      socketService.off(SOCKET_EVENTS.INTERNAL_TICKET.NEW, handleNew)
      socketService.off(SOCKET_EVENTS.INTERNAL_TICKET.REPLY, handleReply)
      socketService.off(SOCKET_EVENTS.INTERNAL_TICKET.RESOLVED, handleResolved)
    }
  }, [])

  const fetchStatuses = async () => {
    setIsLoading(true)
    try {
      const res = await api.get('/admin/status')
      setStatuses(res.data)
    } catch (error) {
      console.error('Error fetching statuses:', error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchStatuses()
  }, [])

  const handleCreate = () => {
    setSelectedStatus(null)
    setFormData({
      name: '',
      color: '#808080',
      impactsSLA: true,
      requiresReason: false,
      description: '',
    })
    onFormOpen()
  }

  const handleEdit = (status: CustomStatus) => {
    setSelectedStatus(status)
    setFormData({
      name: status.name,
      color: status.color,
      impactsSLA: status.impactsSLA,
      requiresReason: status.requiresReason,
      description: status.description || '',
    })
    onFormOpen()
  }

  const handleDelete = (status: CustomStatus) => {
    setSelectedStatus(status)
    onDeleteOpen()
  }

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast({ title: 'Nome é obrigatório', status: 'warning', duration: 3000 })
      return
    }

    try {
      if (selectedStatus) {
        await api.put(`/admin/status/${selectedStatus.id}`, formData)
        toast({ title: 'Status atualizado', status: 'success', duration: 3000 })
      } else {
        await api.post('/admin/status', formData)
        toast({ title: 'Status criado', status: 'success', duration: 3000 })
      }
      onFormClose()
      fetchStatuses()
    } catch (error: any) {
      toast({
        title: 'Erro',
        description: error.response?.data?.error || 'Tente novamente',
        status: 'error',
        duration: 5000,
      })
    }
  }

  const handleConfirmDelete = async () => {
    if (!selectedStatus) return

    try {
      await api.delete(`/admin/status/${selectedStatus.id}`)
      toast({ title: 'Status excluído', status: 'success', duration: 3000 })
      onDeleteClose()
      fetchStatuses()
    } catch (error: any) {
      toast({
        title: 'Erro ao excluir',
        description: error.response?.data?.error || 'Tente novamente',
        status: 'error',
        duration: 5000,
      })
    }
  }

  return (
    <>
      {/* Mobile menu drawer */}
      {isMobile && (
        <Drawer isOpen={isMenuOpen} placement="left" onClose={onMenuClose}>
          <DrawerOverlay />
          <DrawerContent bg={surfaceBg} maxW="280px">
            <DrawerCloseButton />
            <DrawerBody p={0}>
              <Sidebar onClose={onMenuClose} internalTicketsBadge={internalTicketsBadge} />
            </DrawerBody>
          </DrawerContent>
        </Drawer>
      )}

      <Box p={{ base: 3, md: 6 }} h="100%" overflowY="auto">
        <VStack align="stretch" spacing={{ base: 4, md: 6 }}>
          <HStack justify="space-between" spacing={3} flexWrap={{ base: 'wrap', md: 'nowrap' }}>
            <HStack spacing={3} align="center" flex={1} minW={0}>
              {isMobile && (
                <IconButton
                  aria-label="Menu"
                  icon={<List size={20} />}
                  variant="ghost"
                  onClick={onMenuOpen}
                  size="sm"
                  color={menuIconColor}
                  _hover={{ bg: borderColor }}
                  flexShrink={0}
                />
              )}
              <VStack align="start" spacing={0} flex={1} minW={0}>
                <Text fontSize={{ base: 'lg', md: '2xl' }} fontWeight="bold" color={textPrimary}>
                  Status Customizados
                </Text>
                <Text fontSize={{ base: 'xs', md: 'sm' }} color={textMuted} display={{ base: 'none', md: 'block' }}>
                  Configure status que não impactam no SLA
                </Text>
              </VStack>
            </HStack>
            <Button
              leftIcon={<Plus size={18} />}
              colorScheme="yellow"
              onClick={handleCreate}
              size={{ base: 'sm', md: 'md' }}
              flexShrink={0}
            >
              <Text display={{ base: 'none', sm: 'block' }}>Novo Status</Text>
              <Text display={{ base: 'block', sm: 'none' }}>Novo</Text>
            </Button>
          </HStack>

        {isLoading ? (
          <Spinner size="xl" color="yellow.500" alignSelf="center" />
        ) : isMobile ? (
          <VStack spacing={3} align="stretch">
            {statuses.map((status) => (
              <Box
                key={status.id}
                bg={surfaceBg}
                p={4}
                borderRadius="lg"
                border="1px"
                borderColor={borderColor}
              >
                <VStack align="stretch" spacing={3}>
                  <HStack justify="space-between" align="start">
                    <HStack spacing={2} flex={1} minW={0}>
                      <Box w={4} h={4} bg={status.color} borderRadius="md" flexShrink={0} />
                      <Badge bg={status.color} color="white" px={2} py={0.5} fontSize="xs">
                        {status.name}
                      </Badge>
                    </HStack>
                    <HStack spacing={1} flexShrink={0}>
                      <IconButton
                        aria-label="Editar"
                        icon={<PencilSimple size={16} />}
                        size="xs"
                        variant="ghost"
                        onClick={() => handleEdit(status)}
                      />
                      <IconButton
                        aria-label="Excluir"
                        icon={<Trash size={16} />}
                        size="xs"
                        variant="ghost"
                        colorScheme="red"
                        onClick={() => handleDelete(status)}
                      />
                    </HStack>
                  </HStack>
                  <SimpleGrid columns={2} spacing={2}>
                    <HStack justify="space-between">
                      <Text fontSize="xs" color={textMuted}>
                        Impacta SLA:
                      </Text>
                      <Badge colorScheme={status.impactsSLA ? 'red' : 'green'} fontSize="xs">
                        {status.impactsSLA ? 'Sim' : 'Não'}
                      </Badge>
                    </HStack>
                    <HStack justify="space-between">
                      <Text fontSize="xs" color={textMuted}>
                        Requer Motivo:
                      </Text>
                      <Badge colorScheme={status.requiresReason ? 'yellow' : 'gray'} fontSize="xs">
                        {status.requiresReason ? 'Sim' : 'Não'}
                      </Badge>
                    </HStack>
                  </SimpleGrid>
                  <HStack justify="space-between" align="center">
                    <Text fontSize="xs" color={textMuted}>
                      Atendimentos:
                    </Text>
                    <Badge colorScheme="blue" fontSize="xs">
                      {status.chatCount}
                    </Badge>
                  </HStack>
                </VStack>
              </Box>
            ))}
          </VStack>
        ) : (
          <Box bg={surfaceBg} borderRadius="xl" overflow="hidden" overflowX="auto">
            <Table>
              <Thead>
                <Tr>
                  <Th color={textMuted}>Nome</Th>
                  <Th color={textMuted}>Cor</Th>
                  <Th color={textMuted}>Impacta SLA</Th>
                  <Th color={textMuted}>Requer Motivo</Th>
                  <Th color={textMuted} isNumeric>Atendimentos</Th>
                  <Th color={textMuted} w="100px">Ações</Th>
                </Tr>
              </Thead>
              <Tbody>
                {statuses.map((status) => (
                  <Tr key={status.id}>
                    <Td>
                      <Badge bg={status.color} color="white" px={3} py={1}>
                        {status.name}
                      </Badge>
                    </Td>
                    <Td>
                      <Box w={6} h={6} bg={status.color} borderRadius="md" />
                    </Td>
                    <Td>
                      <Badge colorScheme={status.impactsSLA ? 'red' : 'green'}>
                        {status.impactsSLA ? 'Sim' : 'Não'}
                      </Badge>
                    </Td>
                    <Td>
                      <Badge colorScheme={status.requiresReason ? 'yellow' : 'gray'}>
                        {status.requiresReason ? 'Sim' : 'Não'}
                      </Badge>
                    </Td>
                    <Td isNumeric>
                      <Badge colorScheme="blue">{status.chatCount}</Badge>
                    </Td>
                    <Td>
                      <HStack spacing={2}>
                        <IconButton
                          aria-label="Editar"
                          icon={<PencilSimple size={18} />}
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEdit(status)}
                        />
                        <IconButton
                          aria-label="Excluir"
                          icon={<Trash size={18} />}
                          size="sm"
                          variant="ghost"
                          colorScheme="red"
                          onClick={() => handleDelete(status)}
                        />
                      </HStack>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        )}
      </VStack>

      {/* Form Modal */}
      <Modal isOpen={isFormOpen} onClose={onFormClose} size={{ base: 'full', sm: 'lg' }}>
        <ModalOverlay />
        <ModalContent bg={surfaceBg}>
          <ModalHeader>{selectedStatus ? 'Editar' : 'Novo'} Status</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl>
                <FormLabel>Nome</FormLabel>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ex: Aguardando Transportadora"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Cor</FormLabel>
                <HStack>
                  <Input
                    type="color"
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    w="60px"
                    p={1}
                    h="40px"
                  />
                  <Input
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    placeholder="#808080"
                    flex={1}
                  />
                </HStack>
              </FormControl>

              <FormControl>
                <FormLabel>Descrição (opcional)</FormLabel>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Descreva quando este status deve ser usado"
                />
              </FormControl>

              <FormControl display="flex" alignItems="center" justifyContent="space-between">
                <FormLabel mb={0}>
                  <VStack align="start" spacing={0}>
                    <Text>Impacta no SLA</Text>
                    <Text fontSize="xs" color={textMuted}>
                      Se desativado, o tempo neste status não conta para métricas
                    </Text>
                  </VStack>
                </FormLabel>
                <Switch
                  isChecked={formData.impactsSLA}
                  onChange={(e) => setFormData({ ...formData, impactsSLA: e.target.checked })}
                  colorScheme="yellow"
                />
              </FormControl>

              <FormControl display="flex" alignItems="center" justifyContent="space-between">
                <FormLabel mb={0}>
                  <VStack align="start" spacing={0}>
                    <Text>Requer Motivo</Text>
                    <Text fontSize="xs" color={textMuted}>
                      Atendente precisa informar motivo ao usar este status
                    </Text>
                  </VStack>
                </FormLabel>
                <Switch
                  isChecked={formData.requiresReason}
                  onChange={(e) => setFormData({ ...formData, requiresReason: e.target.checked })}
                  colorScheme="yellow"
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onFormClose}>
              Cancelar
            </Button>
            <Button colorScheme="yellow" onClick={handleSubmit}>
              {selectedStatus ? 'Salvar' : 'Criar'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation */}
      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent bg={surfaceBg}>
            <AlertDialogHeader>Excluir Status</AlertDialogHeader>
            <AlertDialogBody>
              <VStack align="start" spacing={2}>
                <Text>Tem certeza que deseja excluir o status "{selectedStatus?.name}"?</Text>
                {selectedStatus && selectedStatus.chatCount > 0 && (
                  <Text color="yellow.400" fontSize="sm">
                    {selectedStatus.chatCount} atendimento(s) serão atualizados para remover este status.
                  </Text>
                )}
              </VStack>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteClose}>
                Cancelar
              </Button>
              <Button colorScheme="red" onClick={handleConfirmDelete} ml={3}>
                Excluir
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
      </Box>
    </>
  )
}
